import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import DailyPerformanceSalesHoursPanel from "@/pages/DailyPerformanceSalesHoursPanel";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Recycle, Trash2, Users, Wrench, Check, ArrowLeft, ClipboardList, Plus,
    Pencil, Package, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { apiErrorMessage } from "@/lib/apiErrorMessage";

const CATEGORIES = [
    { key: "reused",                label: "Reused",
        description: "Will be served again",
        icon: Recycle,  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { key: "waste",                 label: "Waste",
        description: "Discarded — counted as loss",
        icon: Trash2,   tone: "bg-rose-50 text-rose-700 border-rose-200" },
    { key: "staff_meal",            label: "Staff Meal",
        description: "Consumed by team — cost transfer",
        icon: Users,    tone: "bg-amber-50 text-amber-700 border-amber-200" },
    { key: "technical_adjustment",  label: "Technical Adjustment",
        description: "Recipe / portion variance (requires note)",
        icon: Wrench,   tone: "bg-sky-50 text-sky-700 border-sky-200" },
];

const catLabel = (c) => i18n.t(`production.cat.${c.key}`, c.label);
const catDesc = (c) => i18n.t(`production.catDesc.${c.key}`, c.description);

const todayIso = () => new Date().toISOString().slice(0, 10);

function num(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

export default function ProductionReview() {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const { t } = useTranslation();

    const [serviceDate, setServiceDate] = useState(todayIso());
    const [dayDishes, setDayDishes] = useState([]);
    const [history, setHistory] = useState([]);
    const [editor, setEditor] = useState(null);   // { dish, recipe_id, dish_name, review? }
    const [detail, setDetail] = useState(null);   // confirmed review detail
    const [offMenuOpen, setOffMenuOpen] = useState(false); // Dish|Product chooser

    const load = useCallback(async () => {
        if (!restaurantId) return;
        // Launch Audit R1 §5 — auto-create draft reviews for today's
        // planned dishes so the chef sees them immediately without a
        // manual "Start review" step. Idempotent on the backend.
        try {
            await api.post("/production-reviews/auto-load-today", null,
                { params: { ...params, service_date: serviceDate } });
        } catch (_e) { /* silent — endpoint is idempotent, safe to skip */ }
        const [td, hist] = await Promise.all([
            api.get("/production-reviews/today-dishes",
                { params: { ...params, service_date: serviceDate } }),
            api.get("/production-reviews", { params }),
        ]);
        setDayDishes(td.data.dishes || []);
        setHistory(hist.data || []);
    }, [restaurantId, serviceDate, params]);
    useEffect(() => { load(); }, [load]);

    const startReview = (dish) => {
        // Launch Fix R3 §2 — a *confirmed* review is immutable and opens
        // the read-only detail dialog. A *draft* review (auto-created from
        // Weekly Menu with produced pre-filled to the planned portions) is
        // fully editable — open the editor pre-loaded with its current
        // values so the chef only tweaks the deltas before Confirm.
        if (dish.review && dish.review.status === "confirmed") {
            setDetail(dish.review);
            return;
        }
        setEditor({
            weekly_menu_dish_id: dish.id,
            recipe_id: dish.recipe_id || null,
            dish_name: dish.dish_name,
            planned_pax: dish.planned_pax || 0,
            service_date: serviceDate,
            existing: dish.review || null, // draft to hydrate + PATCH on confirm
        });
    };

    const startAdHoc = () => {
        setEditor({
            weekly_menu_dish_id: null, recipe_id: null,
            dish_name: "", planned_pax: 0,
            service_date: serviceDate, ad_hoc: true,
        });
    };

    const startAdHocProduct = () => {
        setEditor({
            weekly_menu_dish_id: null, recipe_id: null,
            dish_name: "", planned_pax: 0,
            service_date: serviceDate, ad_hoc: true,
            entry_kind: "product",
        });
    };

    const editDraft = (r) => {
        // Hydrate the editor with the existing entry. Drafts stay
        // inventory-neutral until Confirm. Confirmed entries route
        // through the PATCH path which reverses and re-applies the
        // canonical `inventory_adjustments` for this review so Waste
        // €, Waste %, and Reports reconcile to the edited value.
        setEditor({
            entry_kind: r.entry_kind || "dish",
            weekly_menu_dish_id: r.weekly_menu_dish_id || null,
            recipe_id: r.recipe_id || null,
            dish_name: r.dish_name || "",
            planned_pax: r.produced_portions || 0,
            service_date: r.service_date,
            existing: r,
            ad_hoc: !r.weekly_menu_dish_id,
        });
    };

    const deleteDraft = async (r) => {
        const isConfirmed = r.status === "confirmed";
        const msg = isConfirmed
            ? t("production.confirmDeleteConfirmed", { defaultValue: 'Delete the confirmed Waste entry for "{{name}}"? The inventory movement will be reversed and Corporate Waste totals will recalculate.', name: r.dish_name })
            : t("production.confirmDeleteDraft", { defaultValue: 'Delete this draft review for "{{name}}"? Drafts never touch inventory, so nothing will change.', name: r.dish_name });
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/production-reviews/${r.id}`);
            toast.success(isConfirmed ? t("production.wasteDeleted", "Waste entry deleted.") : t("production.draftDeleted", "Draft deleted."));
            await load();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    // Daily Performance is an operational data-entry surface and cannot
    // write for "All Restaurants". Restaurant selection is owned by the
    // GLOBAL top-app selector (AppContext) — there is no local dropdown
    // on this page. When no specific restaurant is selected we render a
    // clean empty state so the chef picks one from the global control.
    const hasRestaurant = !!restaurantId;
    if (!hasRestaurant) {
        return (
            <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto"
                 data-testid="production-review-page">
                <PageHeader
                    title={t("production.title", "Daily Performance")}
                    subtitle={t("production.subtitleNoRestaurant", "Sales & Hours and Waste for a single restaurant.")} />
                <div data-testid="dp-select-restaurant-empty"
                     className="rounded-2xl border border-dashed border-neutral-200
                                 bg-white p-10 text-center">
                    <h3 className="text-base font-semibold text-neutral-900 mb-1">
                        {t("production.selectRestaurant", "Select a restaurant")}
                    </h3>
                    <p className="text-sm text-neutral-500 max-w-md mx-auto">
                        {t("production.selectRestaurantHint", "Use the restaurant selector at the top of the app to enter Daily Performance. Data entry is per-restaurant only.")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto" data-testid="production-review-page">
            <PageHeader
                title={t("production.title", "Daily Performance")}
                subtitle={t("production.subtitle", "Sales & Hours and Waste for the currently selected restaurant.")}
                stackOnMobile
                action={
                    <div data-testid="dp-header-actions" className="flex items-center gap-2">
                        <input
                            data-testid="production-review-date"
                            type="date"
                            value={serviceDate}
                            onChange={(e) => setServiceDate(e.target.value)}
                            className="h-10 w-full sm:w-auto px-3 rounded-md border border-neutral-200 text-sm"
                        />
                    </div>
                }
            />

            {/* Sales & Hours */}
            <section className="mb-8" data-testid="dp-sales-hours-section">
                <div className="mb-3 flex items-end justify-between">
                    <h2 className="text-lg font-semibold">{t("production.salesHours", "Sales & Hours")}</h2>
                    <p className="text-xs text-neutral-500">
                        {t("production.salesHoursHint", "Daily business input · saved per calendar day.")}
                    </p>
                </div>
                <DailyPerformanceSalesHoursPanel serviceDate={serviceDate} />
            </section>

            {/* Waste · dishes planned for the day */}
            <section className="mb-10" data-testid="dp-waste-section">
                <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold">{t("production.waste", "Waste")}</h2>
                        <p className="text-xs text-neutral-500">
                            {t("production.wasteHint", { defaultValue: "On-menu dishes for {{date}} · use “Off-menu” for unplanned items.", date: serviceDate })}
                        </p>
                    </div>
                    <button
                        data-testid="production-review-adhoc-button"
                        onClick={() => setOffMenuOpen(true)}
                        className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                    >
                        <Plus className="h-4 w-4" /> {t("production.offMenu", "Off-menu")}
                    </button>
                </div>
                <h3 className="text-sm font-medium text-neutral-500 mb-2"
                    data-testid="dp-waste-on-menu-heading">
                    {t("production.onMenuHeading", { defaultValue: "On menu · dishes on {{date}}", date: serviceDate })}
                </h3>
                {dayDishes.length === 0 ? (
                    <div
                        data-testid="production-review-no-dishes"
                        className="rounded-lg border border-dashed border-neutral-200 p-8 text-center text-sm text-neutral-500"
                    >
                        {t("production.noDishesBefore", "No dishes planned for this date. Use")} <em>{t("production.offMenu", "Off-menu")}</em> {t("production.noDishesAfter", "to record an unplanned dish or product waste.")}
                    </div>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                        {dayDishes.map((d) => (
                            <button
                                key={d.id}
                                data-testid={`production-review-dish-${d.id}`}
                                onClick={() => startReview(d)}
                                className="text-left p-4 rounded-lg border border-neutral-200 bg-white hover:border-neutral-300 transition"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-semibold truncate">{d.dish_name}</div>
                                        <div className="text-xs text-neutral-500 mt-1">
                                            {t("production.plannedPax", { defaultValue: "Planned: {{count}} pax", count: d.planned_pax || 0 })}
                                            {!d.recipe_id && <> · <span className="text-amber-600">{t("production.noRecipeLinked", "no recipe linked")}</span></>}
                                        </div>
                                    </div>
                                    {d.review ? (
                                        <StatusBadge status={d.review.status === "confirmed" ? "completed" : "pending"}>
                                            {d.review.status === "confirmed" ? t("production.reviewed", "Reviewed") : t("production.draft", "Draft")}
                                        </StatusBadge>
                                    ) : (
                                        <StatusBadge status="pending">{t("production.pending", "Pending")}</StatusBadge>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            {/* Recent waste summary — compact, operational view.
                Detailed Produced / Served / Reused / Staff / Tech adj.
                figures remain fully available in the row action's
                detailed review dialog. */}
            <section data-testid="dp-waste-summary-section">
                <h2 className="text-lg font-semibold mb-3">{t("production.recentWaste", "Recent waste")}</h2>
                {history.length === 0 ? (
                    <div className="text-sm text-neutral-500">{t("production.noReviews", "No reviews recorded yet.")}</div>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
                        <table className="w-full text-sm" data-testid="dp-waste-summary-table">
                            <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                                <tr>
                                    <th className="text-left  px-4 py-3" data-testid="dp-waste-col-date">{t("production.col.date", "Date")}</th>
                                    <th className="text-left  px-4 py-3" data-testid="dp-waste-col-product">{t("production.col.productDish", "Product / Dish")}</th>
                                    <th className="text-left  px-4 py-3" data-testid="dp-waste-col-type">{t("production.col.type", "Type")}</th>
                                    <th className="text-right px-4 py-3" data-testid="dp-waste-col-quantity">{t("production.col.quantity", "Quantity")}</th>
                                    <th className="text-right px-4 py-3" data-testid="dp-waste-col-cost">{t("production.col.cost", "Cost")}</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.slice(0, 50).map((r) => {
                                    const isOnMenu = !!r.weekly_menu_dish_id;
                                    const isProduct = r.entry_kind === "product";
                                    // Quantity: canonical waste quantity already
                                    // on the review record (dish → portions from
                                    // `classifications.waste`; product → the
                                    // entered `product_qty` in its own unit).
                                    const qtyValue = isProduct
                                        ? (r.product_qty ?? null)
                                        : (r.classifications?.waste ?? 0);
                                    const qtyUnit = isProduct
                                        ? (r.product_unit || "")
                                        : (qtyValue === 1 ? t("production.portion", "portion") : t("production.portions", "portions"));
                                    // Cost: existing canonical snapshots — never
                                    // recomputed here. Dish uses the confirmed
                                    // `dish_cost_snapshot.total_cost_per_portion
                                    // × classifications.waste`; product uses
                                    // `cost_snapshot.calculated_waste_cost`.
                                    let costEur = null;
                                    if (isProduct) {
                                        costEur = r.cost_snapshot
                                            ?.calculated_waste_cost ?? null;
                                    } else {
                                        const perPortion = r.dish_cost_snapshot
                                            ?.total_cost_per_portion;
                                        if (perPortion != null && qtyValue != null) {
                                            costEur = perPortion * qtyValue;
                                        }
                                    }
                                    return (
                                        <tr key={r.id} className="border-t border-neutral-100"
                                             data-testid={`dp-waste-row-${r.id}`}>
                                            <td className="px-4 py-3">{r.service_date}</td>
                                            <td className="px-4 py-3">{r.dish_name || "—"}</td>
                                            <td className="px-4 py-3">
                                                <StatusBadge
                                                    status={isOnMenu ? "completed" : "pending"}>
                                                    <span data-testid={`dp-waste-type-${r.id}`}>
                                                        {isOnMenu ? t("production.onMenu", "On Menu") : t("production.offMenuBadge", "Off Menu")}
                                                    </span>
                                                </StatusBadge>
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums">
                                                {qtyValue == null || qtyValue === ""
                                                    ? "—"
                                                    : `${qtyValue} ${qtyUnit}`.trim()}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums">
                                                {costEur == null
                                                    ? "—"
                                                    : `€ ${Number(costEur).toFixed(2)}`}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2">
                                                    {r.status === "confirmed" && (
                                                        <button
                                                            data-testid={`production-review-view-${r.id}`}
                                                            onClick={() => setDetail(r)}
                                                            className="text-xs font-semibold text-neutral-600 hover:text-neutral-900"
                                                        >
                                                            {t("production.view", "View")}
                                                        </button>
                                                    )}
                                                    <button
                                                        data-testid={r.status === "confirmed"
                                                            ? `production-review-edit-${r.id}`
                                                            : `production-review-edit-draft-${r.id}`}
                                                        onClick={() => editDraft(r)}
                                                        className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 hover:text-neutral-900"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" /> {t("production.edit", "Edit")}
                                                    </button>
                                                    <button
                                                        data-testid={r.status === "confirmed"
                                                            ? `production-review-delete-${r.id}`
                                                            : `production-review-delete-draft-${r.id}`}
                                                        onClick={() => deleteDraft(r)}
                                                        className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-800"
                                                    >
                                                        <X className="h-3.5 w-3.5" /> {t("production.delete", "Delete")}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {editor && (
                <ReviewEditor
                    seed={editor}
                    onClose={() => setEditor(null)}
                    onDone={async () => { setEditor(null); await load(); }}
                />
            )}
            {offMenuOpen && (
                <OffMenuChooser
                    onCancel={() => setOffMenuOpen(false)}
                    onPickDish={() => { setOffMenuOpen(false); startAdHoc(); }}
                    onPickProduct={() => { setOffMenuOpen(false); startAdHocProduct(); }}
                />
            )}
            {detail && (
                <ReviewDetailDialog review={detail} onClose={() => setDetail(null)} />
            )}
        </div>
    );
}

function ReviewEditor(props) {
    // Dispatch by entry_kind before any hooks — hooks must never be
    // called conditionally, so the two subforms live in dedicated
    // components with their own state.
    const existing = props.seed.existing || null;
    const entryKind = existing?.entry_kind || props.seed.entry_kind || "dish";
    if (entryKind === "product") {
        return <ProductWasteEditor {...props} existing={existing} />;
    }
    return <DishReviewEditor {...props} existing={existing} />;
}

function DishReviewEditor({ seed, existing, onClose, onDone }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();

    const { t } = useTranslation();
    const classif = existing?.classifications || {};

    const [recipes, setRecipes] = useState([]);
    const [recipeId, setRecipeId] = useState(existing?.recipe_id || seed.recipe_id || "");
    const [dishName, setDishName] = useState(existing?.dish_name || seed.dish_name || "");
    // Launch Fix R3 §1 — Produced is pre-filled: from the draft persisted on
    // the backend if one already exists, otherwise from Weekly Menu's
    // planned pax. In both cases the field remains fully editable.
    const [produced, setProduced] = useState(
        existing != null
            ? String(existing.produced_portions ?? seed.planned_pax ?? "")
            : String(seed.planned_pax || ""));
    const [served, setServed] = useState(
        existing != null ? String(existing.served_portions ?? "") : "");
    const [vals, setVals] = useState({
        reused: num(classif.reused),
        waste: num(classif.waste),
        staff_meal: num(classif.staff_meal),
        technical_adjustment: num(classif.technical_adjustment),
    });
    const [techComment, setTechComment] = useState(
        existing?.technical_adjustment_comment || "");
    const [notes, setNotes] = useState(existing?.notes || "");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState("edit"); // edit | preview

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/recipes", { params });
                setRecipes(data || []);
            } catch { /* non-blocking */ }
        })();
    }, [params]);

    const remaining = useMemo(() => Math.max(0, num(produced) - num(served)), [produced, served]);
    const classifiedTotal = useMemo(
        () => vals.reused + vals.waste + vals.staff_meal + vals.technical_adjustment,
        [vals]);
    const diff = Math.round((remaining - classifiedTotal) * 100) / 100;

    const setVal = (k, v) => setVals((p) => ({ ...p, [k]: Math.max(0, num(v)) }));

    const canPreview = (
        num(produced) > 0 &&
        num(served) >= 0 &&
        num(served) <= num(produced) &&
        Math.abs(diff) < 0.0001 &&
        (vals.technical_adjustment === 0 || techComment.trim().length > 0) &&
        dishName.trim().length > 0
    );

    const payload = {
        ...params,
        service_date: seed.service_date,
        recipe_id: recipeId || null,
        weekly_menu_dish_id: seed.weekly_menu_dish_id || null,
        dish_name: dishName.trim(),
        produced_portions: num(produced),
        served_portions: num(served),
        reused: vals.reused,
        waste: vals.waste,
        staff_meal: vals.staff_meal,
        technical_adjustment: vals.technical_adjustment,
        technical_adjustment_comment: techComment.trim(),
        notes: notes.trim(),
    };

    const handlePreview = async () => {
        if (!canPreview) return;
        setBusy(true);
        try {
            const { data } = await api.post("/production-reviews/preview", payload);
            setPreview(data);
            setStep("preview");
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };

    const handleConfirm = async () => {
        setBusy(true);
        try {
            let reviewId = existing?.id;
            const alreadyConfirmed = existing?.status === "confirmed";
            if (reviewId) {
                // Draft already exists (auto-loaded from Weekly Menu) — PATCH
                // it in place so the "produced pre-filled" flow is preserved
                // and we don't leave an orphan zero-quantity draft behind.
                // Confirmed entries also flow through PATCH which now
                // reverses + re-applies inventory adjustments.
                await api.patch(`/production-reviews/${reviewId}`, {
                    produced_portions: num(produced),
                    served_portions: num(served),
                    reused: vals.reused,
                    waste: vals.waste,
                    staff_meal: vals.staff_meal,
                    technical_adjustment: vals.technical_adjustment,
                    technical_adjustment_comment: techComment.trim(),
                    notes: notes.trim(),
                });
            } else {
                const { data: created } = await api.post("/production-reviews", payload);
                reviewId = created.id;
            }
            if (!alreadyConfirmed) {
                await api.post(`/production-reviews/${reviewId}/confirm`);
                toast.success(t("production.reviewConfirmed", "Production review confirmed. Inventory updated."));
            } else {
                toast.success(t("production.reviewUpdated", "Production review updated. Inventory reconciled."));
            }
            await onDone();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="production-review-editor">
                <DialogHeader>
                    <DialogTitle>
                        {step === "edit" ? t("production.editorTitle", "Production Review") : t("production.confirmImpactTitle", "Confirm inventory impact")}
                    </DialogTitle>
                    <DialogDescription>
                        {step === "edit"
                            ? t("production.editorDescription", "Record portions, classify the remainder, then preview the inventory impact.")
                            : t("production.confirmDescription", "Review the ingredients that will be decremented. Inventory is only updated when you confirm.")}
                    </DialogDescription>
                </DialogHeader>

                {step === "edit" ? (
                    <div className="space-y-6">
                        {seed.ad_hoc && (
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <label className="text-xs font-medium text-neutral-600">{t("production.dishName", "Dish name")}</label>
                                    <input
                                        data-testid="production-review-dish-name"
                                        value={dishName}
                                        onChange={(e) => setDishName(e.target.value)}
                                        className="mt-1 h-10 w-full px-3 rounded-md border border-neutral-200 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-neutral-600">{t("production.recipeOptional", "Recipe (optional)")}</label>
                                    <Select value={recipeId || "__none__"} onValueChange={(v) => setRecipeId(v === "__none__" ? "" : v)}>
                                        <SelectTrigger data-testid="production-review-recipe-select" className="mt-1 h-10">
                                            <SelectValue placeholder={t("production.noRecipe", "No recipe")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">{t("production.noRecipe", "No recipe")}</SelectItem>
                                            {recipes.map((r) => (
                                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}

                        {/* Step 1 — Portions */}
                        <section data-testid="production-review-step-portions">
                            <StepHeader n={1} title={t("production.stepPortions", "Portions")}
                                        hint={t("production.stepPortionsHint", "How much was cooked, how much was actually sold.")} />
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <label className="text-sm font-semibold text-neutral-800">
                                        {t("production.produced", "Prepared / Produced")}
                                    </label>
                                    <div className="text-xs text-neutral-500 mb-1">{t("production.producedHint", "Portions you cooked today")}</div>
                                    <input
                                        data-testid="production-review-produced"
                                        type="number" min="0" step="1"
                                        value={produced}
                                        onChange={(e) => setProduced(e.target.value)}
                                        className="h-14 w-full px-3 rounded-md border border-neutral-300 text-2xl font-bold focus:border-neutral-900 focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-neutral-800">
                                        {t("production.served", "Sold / Served")}
                                    </label>
                                    <div className="text-xs text-neutral-500 mb-1">{t("production.servedHint", "Portions actually served to guests")}</div>
                                    <input
                                        data-testid="production-review-served"
                                        type="number" min="0" step="1"
                                        value={served}
                                        onChange={(e) => setServed(e.target.value)}
                                        className="h-14 w-full px-3 rounded-md border border-neutral-300 text-2xl font-bold focus:border-neutral-900 focus:outline-none"
                                    />
                                </div>
                            </div>

                            <div className="mt-3 rounded-lg border-2 border-neutral-900 bg-neutral-900 text-white px-5 py-4 flex items-center justify-between"
                                 data-testid="production-review-remaining-card">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.28em] text-neutral-400">
                                        {t("production.autoCalculated", "Auto-calculated")}
                                    </div>
                                    <div className="text-sm font-semibold">{t("production.remainingToClassify", "Remaining to classify")}</div>
                                </div>
                                <div data-testid="production-review-remaining"
                                     className="text-4xl font-extrabold">
                                    {remaining}
                                </div>
                            </div>
                        </section>

                        {/* Step 2 — Classification */}
                        <section data-testid="production-review-step-classify">
                            <StepHeader n={2} title={t("production.stepClassify", "What happened to the rest?")}
                                        hint={t("production.stepClassifyHint", "Split the remaining portions across the 4 categories. The total must equal the remainder.")} />
                            <div className="grid gap-3 sm:grid-cols-2">
                                {CATEGORIES.map((c) => {
                                    const Icon = c.icon;
                                    return (
                                        <div key={c.key}
                                            className={`p-4 rounded-lg border ${c.tone}`}>
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <div className="flex items-center gap-2 font-semibold">
                                                    <Icon className="h-4 w-4" />
                                                    {catLabel(c)}
                                                </div>
                                            </div>
                                            <p className="text-xs opacity-80 mb-3">{catDesc(c)}</p>
                                            <input
                                                data-testid={`production-review-classification-${c.key}`}
                                                type="number" min="0" step="1"
                                                value={vals[c.key]}
                                                onChange={(e) => setVal(c.key, e.target.value)}
                                                className="h-11 w-full px-3 rounded-md border border-white bg-white text-lg font-semibold text-neutral-900"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {Math.abs(diff) > 0.0001 && (
                                <p data-testid="production-review-breakdown-warning"
                                    className="mt-2 text-xs text-rose-600">
                                    {t("production.classificationMismatch", { defaultValue: "Classifications must equal remaining ({{remaining}}). Difference: {{diff}}.", remaining, diff })}
                                </p>
                            )}

                            {vals.technical_adjustment > 0 && (
                                <div className="mt-3">
                                    <label className="text-xs font-medium text-neutral-600">
                                        {t("production.techComment", "Technical Adjustment comment")} <span className="text-rose-600">*</span>
                                    </label>
                                    <textarea
                                        data-testid="production-review-tech-comment"
                                        value={techComment}
                                        onChange={(e) => setTechComment(e.target.value)}
                                        rows={2}
                                        placeholder={t("production.techPlaceholder", "Explain the variance (recipe yield off, portion size adjusted, etc.)")}
                                        className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-200 text-sm"
                                    />
                                </div>
                            )}
                        </section>

                        {/* Step 3 — Notes */}
                        <section data-testid="production-review-step-notes">
                            <StepHeader n={3} title={t("production.stepNotes", "Notes (optional)")}
                                        hint={t("production.stepNotesHint", "Add anything the head chef or the brigade should remember.")} />
                            <textarea
                                data-testid="production-review-notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 rounded-md border border-neutral-200 text-sm"
                            />
                        </section>
                    </div>
                ) : (
                    <PreviewPanel preview={preview} />
                )}

                <DialogFooter>
                    {step === "edit" ? (
                        <>
                            <button
                                onClick={onClose}
                                className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                                data-testid="production-review-cancel"
                            >{t("common.cancel", "Cancel")}</button>
                            <button
                                onClick={handlePreview}
                                disabled={!canPreview || busy}
                                data-testid="production-review-preview-button"
                                className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-40"
                            >
                                {t("production.previewImpact", "Preview inventory impact")}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setStep("edit")}
                                disabled={busy}
                                data-testid="production-review-back"
                                className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50 inline-flex items-center gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" /> {t("production.back", "Back")}
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={busy}
                                data-testid="production-review-confirm-button"
                                className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"
                            >
                                <Check className="h-4 w-4" /> {t("production.confirmUpdate", "Confirm & update inventory")}
                            </button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PreviewPanel({ preview }) {
    const { t } = useTranslation();
    if (!preview) return null;
    const impact = preview.inventory_impact || [];
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-neutral-200 px-4 py-3 text-sm">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">{t("production.remaining", "Remaining")}</div>
                    <div className="text-2xl font-bold">{preview.remaining_portions}</div>
                </div>
                <div className="rounded-md border border-neutral-200 px-4 py-3 text-sm">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                        {t("production.portionsDeducted", "Portions deducted from inventory")}
                    </div>
                    <div className="text-2xl font-bold">{preview.portions_to_decrement}</div>
                    <div className="text-xs text-neutral-500 mt-1">
                        {t("production.reusedNotDeducted", "Reused portions are not deducted (they will be served later).")}
                    </div>
                </div>
            </div>
            <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">
                    {t("production.ingredientsToDecrement", "Ingredients to decrement")}
                </div>
                {impact.length === 0 ? (
                    <div
                        data-testid="production-review-no-impact"
                        className="rounded-md border border-dashed border-neutral-200 p-4 text-sm text-neutral-500"
                    >
                        {t("production.noImpact", "No ingredients will be decremented (no recipe linked, or only reused portions remain).")}
                    </div>
                ) : (
                    <table className="w-full text-sm border border-neutral-200 rounded-md overflow-hidden">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                            <tr>
                                <th className="text-left px-3 py-2">{t("production.col.ingredient", "Ingredient")}</th>
                                <th className="text-right px-3 py-2">{t("production.col.quantity", "Quantity")}</th>
                                <th className="text-left px-3 py-2">{t("production.col.unit", "Unit")}</th>
                            </tr>
                        </thead>
                        <tbody data-testid="production-review-impact-list">
                            {impact.map((i, idx) => (
                                <tr key={idx} className="border-t border-neutral-100">
                                    <td className="px-3 py-2">{i.ingredient_name}</td>
                                    <td className="px-3 py-2 text-right">{i.quantity}</td>
                                    <td className="px-3 py-2">{i.unit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function ReviewDetailDialog({ review, onClose }) {
    const { t } = useTranslation();
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-xl" data-testid="production-review-detail">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardList className="h-4 w-4" /> {review.dish_name}
                    </DialogTitle>
                    <DialogDescription>
                        {review.service_date} · {review.status === "confirmed" ? t("production.confirmed", "Confirmed") : t("production.draft", "Draft")}
                        {review.confirmed_by && <> · {t("production.byName", { defaultValue: "by {{name}}", name: review.confirmed_by })}</>}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                        <Stat label={t("production.statProduced", "Produced")} value={review.produced_portions} />
                        <Stat label={t("production.statServed", "Served")} value={review.served_portions} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {CATEGORIES.map((c) => (
                            <Stat key={c.key} label={catLabel(c)}
                                value={review.classifications?.[c.key] || 0} />
                        ))}
                    </div>
                    {review.technical_adjustment_comment && (
                        <div className="rounded-md border border-neutral-200 px-3 py-2">
                            <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">
                                {t("production.techNote", "Technical adjustment note")}
                            </div>
                            <div className="text-sm">{review.technical_adjustment_comment}</div>
                        </div>
                    )}
                    {(review.inventory_impact || []).length > 0 && (
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">
                                {t("production.inventoryImpact", "Inventory impact")}
                            </div>
                            <ul className="text-sm space-y-1">
                                {review.inventory_impact.map((i, idx) => (
                                    <li key={idx} className="flex justify-between border-b border-neutral-100 py-1">
                                        <span>{i.ingredient_name}</span>
                                        <span className="font-mono">{i.quantity} {i.unit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <button
                        onClick={onClose}
                        data-testid="production-review-detail-close"
                        className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                    >{t("common.close", "Close")}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StepHeader({ n, title, hint }) {
    return (
        <div className="mb-3 flex items-start gap-3">
            <div className="h-7 w-7 shrink-0 rounded-full bg-neutral-900 text-white grid place-items-center text-xs font-bold">
                {n}
            </div>
            <div>
                <div className="text-sm font-semibold text-neutral-900">{title}</div>
                {hint && <div className="text-xs text-neutral-500">{hint}</div>}
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-md border border-neutral-200 px-3 py-2">
            <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
            <div className="text-xl font-bold">{value}</div>
        </div>
    );
}

// Single-entry Off-menu chooser: routes the chef into either the existing
// dish workflow or the existing Product Waste workflow. No new logic —
// just a light UI hop between the already-shipped editors.
function OffMenuChooser({ onCancel, onPickDish, onPickProduct }) {
    const { t } = useTranslation();
    return (
        <Dialog open onOpenChange={onCancel}>
            <DialogContent className="max-w-md" data-testid="off-menu-chooser">
                <DialogHeader>
                    <DialogTitle>{t("production.offMenuTitle", "Off-menu")}</DialogTitle>
                    <DialogDescription>
                        {t("production.offMenuDescription", "Choose what you're recording. Both flows leave inventory untouched until you confirm.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                        data-testid="off-menu-choose-dish"
                        onClick={onPickDish}
                        className="p-5 rounded-lg border border-neutral-200 bg-white hover:border-neutral-900 transition text-left"
                    >
                        <div className="flex items-center gap-2 font-semibold">
                            <ClipboardList className="h-4 w-4" /> {t("production.dish", "Dish")}
                        </div>
                        <p className="mt-2 text-xs text-neutral-500">
                            {t("production.dishHint", "Log an unplanned dish that was cooked — portions, waste, staff meals and inventory impact.")}
                        </p>
                    </button>
                    <button
                        data-testid="off-menu-choose-product"
                        onClick={onPickProduct}
                        className="p-5 rounded-lg border border-neutral-200 bg-white hover:border-neutral-900 transition text-left"
                    >
                        <div className="flex items-center gap-2 font-semibold">
                            <Package className="h-4 w-4" /> {t("production.product", "Product")}
                        </div>
                        <p className="mt-2 text-xs text-neutral-500">
                            {t("production.productHint", "Log raw-product waste (spoilage, damage, expired) directly against inventory.")}
                        </p>
                    </button>
                </div>
                <DialogFooter>
                    <button
                        data-testid="off-menu-cancel"
                        onClick={onCancel}
                        className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                    >{t("common.cancel", "Cancel")}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─────────────────────────────────────────────────────────────
// Product Waste editor — Prompt 41.
//
// A dedicated single-purpose form. Chef picks a product, enters qty in a
// compatible unit (fetched from the backend so the UI can never offer a
// cross-dimensional unit), and previews the exact inventory movement +
// cost snapshot BEFORE confirming. Drafts remain fully editable and
// inventory-neutral until Confirm.
// ─────────────────────────────────────────────────────────────
function ProductWasteEditor({ seed, existing, onClose, onDone }) {
    const { scopedParams: params } = useRestaurantScope();
    const [products, setProducts] = useState([]);
    const [query, setQuery] = useState("");
    const [product, setProduct] = useState(null);       // full product doc
    const [compatUnits, setCompatUnits] = useState([]); // filtered by canonical
    const [qty, setQty] = useState(existing ? String(existing.product_qty ?? "") : "");
    const [unit, setUnit] = useState(existing?.product_unit || "");
    const [notes, setNotes] = useState(existing?.notes || "");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState("edit");           // edit | preview
    const { t } = useTranslation();

    // Phase 3 · Server-side search (2026-02). Previously this
    // component fetched the first 200 products alphabetically and
    // filtered client-side, which made products beyond that slice
    // unreachable in tenants with large catalogues (Production RCA
    // 2026-02). Now: fetch on demand via `/products?q=` with a small
    // page size + debounce. Legacy client-side `filtered` slice is
    // gone.
    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/products", {
                    params: { ...params, q: query || undefined,
                                status: "available", limit: 50 },
                });
                if (!cancelled) setProducts(data.items || []);
            } catch { if (!cancelled) setProducts([]); }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [query, params]);

    // Hydrate an existing draft: pre-select the product by direct GET.
    const existingProductId = existing?.product_id;
    const existingHasUnit = !!existing?.product_unit;
    useEffect(() => {
        if (!existingProductId) return;
        (async () => {
            try {
                const { data } = await api.get(
                    `/products/${existingProductId}`, { params });
                if (data?.id) setProduct(data);
            } catch { /* non-blocking */ }
        })();
    }, [existingProductId, params]);

    // Fetch compatible units whenever the selected product changes.
    const productId = product?.id;
    useEffect(() => {
        if (!productId) { setCompatUnits([]); return; }
        (async () => {
            try {
                const { data } = await api.get(
                    `/products/${productId}/compatible-units`,
                    { params });
                setCompatUnits(data.compatible_units || []);
                // Default to the product's canonical unit.
                if (!existingHasUnit) {
                    setUnit(data.canonical_unit || "");
                }
            } catch { setCompatUnits([]); }
        })();
    }, [productId, existingHasUnit, params]);

    // Post-Phase-3: no client-side slice. `products` is already the
    // server's top-50 for the current query.
    const filtered = products;

    const qtyNum = num(qty);
    const canPreview = !!(product && qtyNum > 0 && unit);

    const payload = {
        ...params,
        service_date: seed.service_date,
        entry_kind: "product",
        product_id: product?.id,
        product_qty: qtyNum,
        product_unit: unit,
        notes: notes.trim(),
    };

    const handlePreview = async () => {
        if (!canPreview) return;
        setBusy(true);
        try {
            const { data } = await api.post("/production-reviews/preview", payload);
            setPreview(data);
            setStep("preview");
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };

    const handleConfirm = async () => {
        setBusy(true);
        try {
            let reviewId = existing?.id;
            const alreadyConfirmed = existing?.status === "confirmed";
            if (reviewId) {
                await api.patch(`/production-reviews/${reviewId}`, {
                    product_qty: qtyNum, product_unit: unit,
                    notes: notes.trim(),
                });
            } else {
                const { data: created } = await api.post("/production-reviews",
                    payload);
                reviewId = created.id;
            }
            if (!alreadyConfirmed) {
                await api.post(`/production-reviews/${reviewId}/confirm`);
                toast.success(t("production.productWasteConfirmed", "Product waste confirmed. Inventory updated."));
            } else {
                toast.success(t("production.productWasteUpdated", "Product waste updated. Inventory reconciled."));
            }
            await onDone();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto"
                             data-testid="product-waste-editor">
                <DialogHeader>
                    <DialogTitle>
                        {step === "edit" ? t("production.productWasteTitle", "Off-menu product waste")
                                             : t("production.confirmImpactTitle", "Confirm inventory impact")}
                    </DialogTitle>
                    <DialogDescription>
                        {step === "edit"
                            ? t("production.productWasteDescription", "Log a raw-product waste event against Inventory. Only stock-unit compatible units are offered.")
                            : t("production.productWasteConfirmDescription", "Review the inventory movement and cost snapshot. Nothing is written until you confirm.")}
                    </DialogDescription>
                </DialogHeader>

                {step === "edit" ? (
                    <div className="space-y-5">
                        {/* Product picker */}
                        <div>
                            <label className="text-xs font-medium text-neutral-600">
                                {t("production.product", "Product")}
                            </label>
                            {product ? (
                                <div className="mt-1 flex items-center justify-between rounded-md border border-neutral-200 px-3 py-2 bg-neutral-50">
                                    <div>
                                        <div data-testid="product-waste-selected-name"
                                             className="text-sm font-semibold">
                                            {product.product_name}
                                        </div>
                                        <div className="text-xs text-neutral-500">
                                            {t("production.stockUnit", { defaultValue: "Stock unit: {{unit}}", unit: product.unit || "—" })}
                                            {product.last_price
                                                ? t("production.lastPrice", { defaultValue: " · Last price €{{price}}/{{unit}}", price: product.last_price, unit: product.unit })
                                                : t("production.noKnownCost", " · No known cost")}
                                        </div>
                                    </div>
                                    <button
                                        data-testid="product-waste-change-product"
                                        onClick={() => { setProduct(null); setPreview(null); }}
                                        className="text-xs font-semibold text-neutral-600 hover:text-neutral-900"
                                    >
                                        {t("production.change", "Change")}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <input
                                        data-testid="product-waste-search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder={t("production.searchPlaceholder", "Search by name or barcode…")}
                                        className="mt-1 h-10 w-full px-3 rounded-md border border-neutral-200 text-sm"
                                    />
                                    <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-neutral-100">
                                        {filtered.length === 0 ? (
                                            <div className="p-3 text-xs text-neutral-500">
                                                {t("production.noProducts", "No products found.")}
                                            </div>
                                        ) : filtered.map(p => (
                                            <button
                                                key={p.id}
                                                data-testid={`product-waste-pick-${p.id}`}
                                                onClick={() => setProduct(p)}
                                                className="w-full text-left px-3 py-2 hover:bg-neutral-50 border-b border-neutral-100 last:border-0"
                                            >
                                                <div className="text-sm font-medium truncate">
                                                    {p.product_name}
                                                </div>
                                                <div className="text-xs text-neutral-500">
                                                    {p.unit || "—"} · {p.category || "—"}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Quantity + Unit */}
                        {product && (
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <label className="text-xs font-medium text-neutral-600">
                                        {t("production.wastedQuantity", "Wasted quantity")}
                                    </label>
                                    <input
                                        data-testid="product-waste-qty"
                                        type="number" min="0" step="0.001"
                                        value={qty}
                                        onChange={(e) => setQty(e.target.value)}
                                        className="mt-1 h-12 w-full px-3 rounded-md border border-neutral-300 text-xl font-bold focus:border-neutral-900 focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-neutral-600">
                                        {t("production.unit", "Unit")}
                                    </label>
                                    <Select value={unit} onValueChange={setUnit}>
                                        <SelectTrigger data-testid="product-waste-unit"
                                                         className="mt-1 h-12">
                                            <SelectValue placeholder={t("production.unit", "Unit")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {compatUnits.map(u => (
                                                <SelectItem key={u} value={u}>{u}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="text-[11px] text-neutral-500 mt-1">
                                        {t("production.compatibleUnitsBefore", "Only units compatible with")} <b>{product.unit}</b> {t("production.compatibleUnitsAfter", "are offered.")}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        <div>
                            <label className="text-xs font-medium text-neutral-600">
                                {t("production.notesOptional", "Notes (optional)")}
                            </label>
                            <textarea
                                data-testid="product-waste-notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-200 text-sm"
                                placeholder={t("production.wasteReasonPlaceholder", "Why was this product wasted (spoilage, damage, expired…)?")}
                            />
                        </div>
                    </div>
                ) : (
                    <ProductWastePreview preview={preview} product={product} />
                )}

                <DialogFooter>
                    {step === "edit" ? (
                        <>
                            <button onClick={onClose}
                                className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                                data-testid="product-waste-cancel"
                            >{t("common.cancel", "Cancel")}</button>
                            <button
                                onClick={handlePreview}
                                disabled={!canPreview || busy}
                                data-testid="product-waste-preview-button"
                                className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-40"
                            >
                                {t("production.previewImpact", "Preview inventory impact")}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setStep("edit")}
                                disabled={busy}
                                data-testid="product-waste-back"
                                className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50 inline-flex items-center gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" /> {t("production.back", "Back")}
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={busy}
                                data-testid="product-waste-confirm-button"
                                className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"
                            >
                                <Check className="h-4 w-4" /> {t("production.confirmUpdate", "Confirm & update inventory")}
                            </button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ProductWastePreview({ preview, product }) {
    const { t } = useTranslation();
    if (!preview) return null;
    const row = (preview.inventory_impact || [])[0] || {};
    const snap = preview.cost_snapshot || {};
    const hasCost = snap.calculated_waste_cost != null;
    return (
        <div className="space-y-4">
            <div className="rounded-md border border-neutral-200 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                    {t("production.movementCanonical", "Inventory movement (canonical unit)")}
                </div>
                <div className="mt-1 text-2xl font-extrabold text-rose-700"
                     data-testid="product-waste-preview-qty">
                    − {row.quantity} {row.unit}
                </div>
                <div className="text-xs text-neutral-500">
                    {t("production.productLabel", "Product:")} <span className="font-semibold">{row.product_name || product?.product_name}</span>
                </div>
                {row.entered_unit && row.entered_unit !== row.unit && (
                    <div className="text-xs text-neutral-500 mt-1">
                        {t("production.enteredAs", { defaultValue: "Entered as {{eq}} {{eu}} · converted to {{q}} {{u}}.", eq: row.entered_quantity, eu: row.entered_unit, q: row.quantity, u: row.unit })}
                    </div>
                )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-md border border-neutral-200 px-4 py-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                        {t("production.costSnapshot", "Cost snapshot")}
                    </div>
                    {hasCost ? (
                        <>
                            <div className="text-xl font-bold"
                                 data-testid="product-waste-preview-cost">
                                € {Number(snap.calculated_waste_cost).toFixed(2)}
                            </div>
                            <div className="text-xs text-neutral-500 mt-1">
                                @ €{snap.unit_cost}/{snap.cost_unit} {t("production.lockedAtConfirm", "· locked at confirm")}
                            </div>
                        </>
                    ) : (
                        <div className="text-sm text-neutral-500 flex items-start gap-2"
                             data-testid="product-waste-preview-cost-unknown">
                            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                            {t("production.costUnknown", "Cost unknown — quantity will be recorded, but no € figure will be shown in Reports (never €0).")}
                        </div>
                    )}
                </div>
                <div className="rounded-md border border-neutral-200 px-4 py-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                        {t("production.approxWeight", "Approx. weight")}
                    </div>
                    <div className="text-xl font-bold"
                         data-testid="product-waste-preview-kg">
                        {preview.approx_waste_kg != null
                            ? `${preview.approx_waste_kg} kg`
                            : "—"}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                        {preview.approx_weight_status === "unavailable"
                            ? t("production.weightUnavailable", "Not derivable — this product's stock unit doesn't convert to kg.")
                            : t("production.weightBased", "Based on legitimate mass conversion — never invented.")}
                    </div>
                </div>
            </div>
        </div>
    );
}
